/* cSpell:disable */
import { HeaderConfig } from '@tenants/types/tenant';
import { LOCALE_EXP_REALTY_US_PATHS } from 'types/locales';
import { ACCESSIBILITY_PATH } from 'constants/urls';

/**
 * Creates a header configuration for the eXp Realty US tenant.
 *
 * This function generates a standardized header configuration that includes:
 * - Main and authentication button text
 * - Authentication menu items (saved searches, favorites, profile, sign out)
 * - Main navigation menu items
 *
 * @returns A complete header configuration object with all necessary navigation and authentication elements
 */
export function createHeaderConfig(): HeaderConfig {
  const headerConfig: HeaderConfig = {
    mainButtonCopy: {
      key: LOCALE_EXP_REALTY_US_PATHS.HEADER_BUTTONS_MAIN_BUTTON,
      defaultValue: 'Find Your Home',
    },
    authButtonCopy: {
      key: LOCALE_EXP_REALTY_US_PATHS.HEADER_BUTTONS_AUTH_BUTTON,
      defaultValue: 'Log in',
    },
    internationalButtonCopy: {
      key: LOCALE_EXP_REALTY_US_PATHS.HEADER_BUTTONS_INTERNATIONAL_BUTTON,
      defaultValue: 'International',
    },
    hasJoinExpPage: true,
    authMenuItems: [
      {
        label: {
          key: LOCALE_EXP_REALTY_US_PATHS.HEADER_AUTH_MENU_ITEMS_MY_SAVED_SEARCHES,
          defaultValue: 'My Saved Searches',
        },
        link: '/account/saved/searches',
        id: 'my-saved-searches',
      },
      {
        label: {
          key: LOCALE_EXP_REALTY_US_PATHS.HEADER_AUTH_MENU_ITEMS_MY_FAVOURITES,
          defaultValue: 'My Favorites',
        },
        link: '/account/saved/favourites',
        id: 'my-favourites',
      },
      {
        label: {
          key: LOCALE_EXP_REALTY_US_PATHS.HEADER_AUTH_MENU_ITEMS_EDIT_PROFILE,
          defaultValue: 'Edit Profile',
        },
        link: '/account/details',
        id: 'edit-profile',
      },
      {
        label: {
          key: LOCALE_EXP_REALTY_US_PATHS.HEADER_AUTH_MENU_ITEMS_SIGN_OUT,
          defaultValue: 'Sign Out',
        },
        id: 'sign-out',
        signOut: true,
      },
    ],
    menuItems: [
      {
        id: 'buy',
        label: {
          key: LOCALE_EXP_REALTY_US_PATHS.HEADER_MENU_ITEMS_BUY,
          defaultValue: 'Buy',
        },
        link: '/buy',
      },
      {
        id: 'sell',
        label: {
          key: LOCALE_EXP_REALTY_US_PATHS.HEADER_MENU_ITEMS_SELL,
          defaultValue: 'Sell',
        },
        link: '/sell',
      },
      {
        id: 'find-an-agent',
        label: {
          key: LOCALE_EXP_REALTY_US_PATHS.HEADER_MENU_ITEMS_FIND_AN_AGENT,
          defaultValue: 'Find An Agent',
        },
        link: '/agents-search',
      },
      {
        id: 'join-exp',
        label: {
          key: LOCALE_EXP_REALTY_US_PATHS.HEADER_MENU_ITEMS_JOIN_EXP,
          defaultValue: 'Join eXp',
        },
        link: 'https://join.exprealty.com',
        newTab: true,
      },
      {
        id: 'agent-login',
        label: {
          key: LOCALE_EXP_REALTY_US_PATHS.HEADER_MENU_ITEMS_AGENT_LOGIN,
          defaultValue: 'Agent Login',
        },
        link: 'https://hub.exprealty.com/',
        newTab: true,
      },
      {
        id: 'about',
        label: {
          key: LOCALE_EXP_REALTY_US_PATHS.HEADER_MENU_ITEMS_ABOUT,
          defaultValue: 'About',
        },
        link: '/explore-exp',
      },
      {
        id: 'accessibility',
        label: {
          key: LOCALE_EXP_REALTY_US_PATHS.HEADER_MENU_ITEMS_ACCESSIBILITY,
          defaultValue: 'Accessibility',
        },
        link: ACCESSIBILITY_PATH,
      },
      {
        id: 'sitemap',
        label: {
          key: LOCALE_EXP_REALTY_US_PATHS.HEADER_MENU_ITEMS_SITEMAP,
          defaultValue: 'Sitemap',
        },
        link: '/sitemap',
      },
    ],
    buttonLabels: {
      imReady: {
        key: LOCALE_EXP_REALTY_US_PATHS.HEADER_BUTTONS_IM_READY,
        defaultValue: "I'm Ready",
      },
      contactUs: {
        key: LOCALE_EXP_REALTY_US_PATHS.HEADER_BUTTONS_CONTACT_US,
        defaultValue: 'Contact Us',
      },
      mainButton: {
        key: LOCALE_EXP_REALTY_US_PATHS.HEADER_BUTTONS_MAIN_BUTTON,
        defaultValue: 'Find Your Home',
      },
      authButton: {
        key: LOCALE_EXP_REALTY_US_PATHS.HEADER_BUTTONS_AUTH_BUTTON,
        defaultValue: 'Log in',
      },
    },
  };

  return headerConfig;
}
